import { Box, IconButton, Tooltip, CircularProgress } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ModalComponent from "../../UI/Modal";
import { IExamsRedirectFileProps } from "./models";
import ExamsRedirectModal from "./ExamsRedirectModal";

import useFetch from "../../../hooks/useFetch";
import {
  getLaboratorialExamsFile,
  getLaboratorialExamsFileAsTemporary,
} from "../../../services/laboratorialExams.service";
import { tokenTemporaryService } from "../../../services/tokenTemorary.service";

import { useExtensionConfigs } from "../../../hooks/useExtensionConfigs";
import { Extension } from "../../../models/intalled-extensions";

const translationPath = "components.examsRedirectFile.";

const ExamsRedirectFile = ({
  sequencialImageControls,
  sequencialControl,
}: IExamsRedirectFileProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const {
    sendRequest,
    loading: labLoading,
  } = useFetch(getLaboratorialExamsFile);

  const {
    sendRequest: getTemporaryFile,
    loading: tempLoading,
  } = useFetch(getLaboratorialExamsFileAsTemporary);

  const { fetchExtensionConfig } = useExtensionConfigs();
  const [cdnUrl, setCdnUrl] = useState<boolean>(false);

  const isLoading = labLoading || tempLoading;

  useEffect(() => {
    const fetchExtensions = async () => {
      const configCdnUrl = await fetchExtensionConfig(
        Extension.EXAMS,
        "CDNURL"
      );
      if (!configCdnUrl || (configCdnUrl && configCdnUrl.value === ".")) {
        setCdnUrl(false);
      } else {
        setCdnUrl(true);
      }
    };

    fetchExtensions();
  }, [fetchExtensionConfig]);

  const handleCloseModal = () => setOpen(false);

  const handleClick = useCallback(async () => {
    if (isLoading) return;

    if (sequencialImageControls.length > 1) {
      setOpen(true);
      return;
    }

    if (sequencialImageControls.length === 0) return;

    const sequential = sequencialImageControls[0];

    const fileNameArray = sequential.medicalCertificateFile.split(".");
    const format = fileNameArray[fileNameArray.length - 1];

    const isLogginTemporary =
      !!tokenTemporaryService.getTemporaryAccessToken();

    let response;

    if (isLogginTemporary) {
      response = await getTemporaryFile({
        Identifier: sequencialControl,
        ImageIdentifier: sequential.sequencialImageControl,
      });
    } else {
      response = await sendRequest({
        Identifier: sequencialControl,
        ImageIdentifier: sequential.sequencialImageControl,
      });
    }

    if (!response) return;

    const { data, success } = response;

    if (!data || !success) return;

    if (cdnUrl) {
      window.open(data, "_blank");
      return;
    }

    const mimeTypeMap: Record<string, string> = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      txt: "text/plain",
      pdf: "application/pdf",
      zip: "application/zip",
    };

    try {
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: mimeTypeMap[format.toLowerCase()] || "application/pdf",
      });

      const blobUrl = URL.createObjectURL(blob);

      const newTab = window.open();
      if (!newTab) return;

      newTab.location.href = blobUrl;
    } catch (error) {
      setOpen(true);
    }
  }, [
    sequencialImageControls,
    sequencialControl,
    cdnUrl,
    sendRequest,
    getTemporaryFile,
    isLoading,
  ]);

  return (
    <>
      <ModalComponent open={open} onClose={handleCloseModal} dialogMaxWidth="xl">
        <ExamsRedirectModal
          sequencialControl={sequencialControl}
          sequencialImageControls={sequencialImageControls}
        />
      </ModalComponent>

      <Tooltip title={t(translationPath + "download_image")} placement="left">
        <Box>
          <IconButton onClick={handleClick} size="small" disabled={isLoading}>
            {isLoading ? (
              <CircularProgress size={18} />
            ) : (
              <OpenInNewIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Tooltip>
    </>
  );
};

export default memo(ExamsRedirectFile);
